import type { Logger } from '@vp/ubik-logging'

import type { MdoAvailabilityResponse } from '../types/MerchantDataOrchestration'

const baseUrl = 'https://merchant-data-orchestration.products.cimpress.io'

/**
 * Fetches the cross-size minimum order quantity evaluated by Merchant Data Orchestration (MDO)
 * for the given product, context, and selections. MDO evaluates the MOQ via the Merchant
 * Quantity Service (MQS) and returns it alongside availability data.
 *
 * This call is non-blocking: if MDO has no matching MOQ rule (null) or the request fails for
 * any reason (network error, non-2xx response), `null` is returned so callers can fall back to
 * their existing quantity-constraint-based MOQ.
 */
export const getMinimumOrderQuantity = async (
  merchant: string,
  productKey: string,
  productVersion: number,
  country: string,
  selections: Record<string, string>,
  authorizationHeader: string,
  logger: Logger,
): Promise<number | null> => {
  try {
    if (!productKey || !productVersion) {
      throw new Error('Missing product info')
    }

    const searchParams = buildQueryParams(productVersion, merchant, country, selections)
    const url = `${baseUrl}/v1/products/${productKey}/availability?${searchParams.toString()}`
    const headers = { Authorization: authorizationHeader }
    const request = new Request(url, { headers })

    const response = await fetch(request)

    if (!response.ok) {
      throw new Error(`Request to ${url} failed with status: ${response.status}`)
    }

    const data: MdoAvailabilityResponse = await response.json()
    return data._embedded?.minimumOrderQuantity ?? null
  } catch (error) {
    logger.warn('Cannot get minimum order quantity from Merchant Data Orchestration for productKey', {
      productKey,
      productVersion,
      country,
      errorMessage: (error as Error).message,
      errorStack: (error as Error).stack,
    })
    return null
  }
}

const buildQueryParams = (productVersion: number, merchant: string, country: string, selections: Record<string, string>) => {
  const searchParams = new URLSearchParams()

  searchParams.append('productVersion', String(productVersion))

  for (const [key, value] of Object.entries({ Merchant: merchant, Country: country })) {
    searchParams.append(`context[${key}]`, String(value))
  }

  for (const [key, value] of Object.entries(selections)) {
    searchParams.append(`selections[${key}]`, String(value))
  }

  return searchParams
}
